// Demand-based reorder panel, surfaced on the Products page.
// Uses GET /products/reports/reorder (avg daily sales over 30 days → days to
// stockout → suggested order qty) and lets the user create a PO to restock each
// product with one click (prefills the PO create page).

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Modal, DataTable, Badge, Button, Skeleton, EmptyState } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { apiClient } from '@/lib/api-client';
import { formatCurrency } from '@/utils/currency';
import type { CurrencyCode } from '@bbpos/types';

interface ReorderModalProps {
  open: boolean;
  onClose: () => void;
  locationId?: string;
  currency?: CurrencyCode;
}

// Shape returned by /products/reports/reorder (see products.repository reorderReport).
interface ReorderRow {
  id: string;
  name: string;
  sku: string;
  stock_quantity: number;
  low_stock_threshold: number;
  avg_daily_sales: number;
  days_to_stockout: number | null;
  suggested_order_qty: number;
  urgency: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  /** Last received cost × suggested qty; null when the product was never received. */
  estimated_order_cost: number | null;
}

const URGENCY_META: Record<ReorderRow['urgency'], { label: string; variant: 'danger' | 'warning' | 'info' | 'success' }> = {
  CRITICAL: { label: 'Critical', variant: 'danger' },
  HIGH: { label: 'High', variant: 'warning' },
  MEDIUM: { label: 'Medium', variant: 'info' },
  LOW: { label: 'Low', variant: 'success' },
};

export function ReorderModal({ open, onClose, locationId, currency = 'INR' }: ReorderModalProps) {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['products', 'reorder', locationId ?? ''],
    queryFn: () =>
      // The endpoint returns { products, total_products, total_estimated_cost }
      // (unwrapped from the envelope by apiClient), NOT a paginated list.
      apiClient<{ products: ReorderRow[]; total_products: number; total_estimated_cost: number }>(
        `/products/reports/reorder${locationId ? `?location_id=${encodeURIComponent(locationId)}` : ''}`,
      ),
    enabled: open,
    staleTime: 30_000,
  });

  const rows = data?.products ?? [];

  function createPO(row: ReorderRow) {
    const qs = new URLSearchParams({
      product_id: row.id,
      product_name: row.name,
      sku: row.sku,
      quantity: String(row.suggested_order_qty),
    });
    navigate(`/inventory/purchase-orders/new?${qs.toString()}`);
  }

  const columns: ColumnDef<ReorderRow>[] = [
    { key: 'name', header: 'Product', render: (_v, r) => (
      <div><p className="text-sm font-semibold">{r.name}</p><p className="text-xs text-neutral-400">{r.sku}</p></div>
    ) },
    { key: 'stock_quantity', header: 'In stock', numeric: true, render: (_v, r) => <span className="tabular-nums">{r.stock_quantity}</span> },
    { key: 'days_to_stockout', header: 'Days left', numeric: true, render: (_v, r) => (
      // Whole days only — floor so we don't overstate runway before a stockout.
      <span className="tabular-nums">{r.days_to_stockout == null ? '—' : `${Math.floor(r.days_to_stockout)}d`}</span>
    ) },
    { key: 'suggested_order_qty', header: 'Suggested qty', numeric: true, render: (_v, r) => (
      <span className="tabular-nums font-semibold text-brand-600">{r.suggested_order_qty}</span>
    ) },
    { key: 'estimated_order_cost', header: 'Est. cost', numeric: true, render: (_v, r) => (
      <span className="tabular-nums">
        {r.estimated_order_cost == null ? '—' : formatCurrency(Number(r.estimated_order_cost), currency)}
      </span>
    ) },
    { key: 'urgency', header: 'Urgency', render: (_v, r) => {
      const m = URGENCY_META[r.urgency];
      return <Badge variant={m.variant} size="sm">{m.label}</Badge>;
    } },
    { key: 'action', header: '', render: (_v, r) => (
      <Button variant="primary" size="sm" onClick={() => createPO(r)}>Create PO</Button>
    ) },
  ];

  return (
    <Modal open={open} onClose={onClose} title="Reorder — Demand Forecast" variant="xl">
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
        Products ranked by how soon they run out (based on the last 30 days of sales). “Suggested qty”
        covers the lead time. Click “Create PO” to raise a purchase order to restock.
      </p>
      {isLoading ? (
        <Skeleton variant="rect" height={280} className="rounded-lg" />
      ) : rows.length === 0 ? (
        <EmptyState variant="page" title="Nothing to reorder" description="No products are running low right now." />
      ) : (
        <DataTable<ReorderRow> data={rows} columns={columns} />
      )}
    </Modal>
  );
}
